import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  DollarSign, 
  Users, 
  Video, 
  CheckCircle2, 
  ArrowRight,
  Sparkles,
  Star,
  TrendingUp,
  Globe,
  Clock
} from 'lucide-react';
import { useAuthStore } from '../store/auth';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const benefits = [
  {
    icon: DollarSign,
    title: 'Earn Revenue',
    description: 'Keep up to 80% of your course earnings. Get paid monthly with transparent revenue sharing.',
  },
  {
    icon: Users,
    title: 'Global Reach',
    description: 'Teach students from around the world. Our platform supports multiple languages and currencies.',
  },
  {
    icon: Video,
    title: 'Live Sessions',
    description: 'Host live classes, Q&A sessions, and interactive workshops with built-in video conferencing.',
  },
  {
    icon: TrendingUp,
    title: 'Analytics Dashboard',
    description: 'Track your performance with detailed analytics on enrollments, revenue, and student engagement.',
  },
  {
    icon: Globe,
    title: 'Marketing Support',
    description: 'Get featured on our homepage, email campaigns, and social media to boost your visibility.',
  },
  {
    icon: Clock,
    title: 'Flexible Schedule',
    description: 'Create courses on your own time. Record once and earn passive income for years.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Apply to Teach',
    description: 'Submit your application with your expertise and course ideas. We review all applications within 48 hours.',
  },
  {
    number: '02',
    title: 'Get Approved',
    description: 'Once approved, you\'ll get access to our instructor dashboard and course creation tools.',
  },
  {
    number: '03',
    title: 'Create Your Course',
    description: 'Use our intuitive tools to create engaging video content, quizzes, and assignments.',
  },
  {
    number: '04',
    title: 'Start Earning',
    description: 'Publish your course and start earning. We handle payments, hosting, and student support.',
  },
];

const testimonials = [
  {
    quote: "Ulearning transformed my teaching career. I went from local workshops to reaching 10,000+ students globally.",
    author: "Dr. Sarah Chen",
    role: "Data Science Instructor",
    earnings: "$45,000+ earned",
    avatar: "SC"
  },
  {
    quote: "The platform's tools make course creation a breeze. I launched my first course in just 2 weeks.",
    author: "Marcus Johnson",
    role: "Web Development Expert",
    earnings: "$32,000+ earned",
    avatar: "MJ"
  },
  {
    quote: "The support team is incredible. They helped me optimize my courses for better student engagement.",
    author: "Elena Rodriguez",
    role: "UX Design Mentor",
    earnings: "$28,000+ earned",
    avatar: "ER"
  },
];

export default function BecomeInstructorPage() {
  const { isAuthenticated, user } = useAuthStore();
  const [formData, setFormData] = useState({
    expertise: '',
    experience: '',
    courseIdea: '',
    linkedin: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/25 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[350px] bg-coral/15 rounded-full blur-[100px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div variants={fadeUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                             bg-coral/15 border border-coral/25
                             text-coral text-sm font-medium">
                <GraduationCap className="w-4 h-4" />
                <span>Share Your Expertise</span>
              </span>
            </motion.div>

            <motion.h1 
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
            >
              Become an{' '}
              <span className="text-gradient-coral">Instructor</span>
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="mt-6 text-lg text-white/50 max-w-2xl mx-auto"
            >
              Join our community of expert instructors and share your knowledge with 
              thousands of eager learners worldwide. Earn while making an impact.
            </motion.p>

            <motion.div 
              variants={fadeUp}
              className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a href="#apply" className="btn-primary group">
                <span>Apply Now</span>
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </a>
              <a href="#benefits" className="btn-secondary">
                Learn More
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div 
              variants={fadeUp}
              className="mt-12 grid grid-cols-3 gap-8 max-w-xl mx-auto"
            >
              {[
                { value: '500+', label: 'Active Instructors' },
                { value: '$2M+', label: 'Paid to Instructors' },
                { value: '100K+', label: 'Students Taught' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-gradient-coral">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs text-white/40 font-medium uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 border-t border-white/[0.06]">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Why Teach on Ulearning?
            </h2>
            <p className="mt-3 text-white/40 max-w-2xl mx-auto">
              We provide everything you need to create, publish, and monetize your expertise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card card-hover group"
              >
                <div className="icon-coral mb-4 group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-5 h-5 text-coral" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              How It Works
            </h2>
            <p className="mt-3 text-white/40">
              From application to first paycheck in 4 simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-px bg-gradient-to-r from-coral/40 to-transparent z-0" />
                )}
                <div className="card text-center relative z-10">
                  <div className="text-4xl font-bold text-coral/20 mb-4">{step.number}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/40 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 border-t border-white/[0.06]">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Hear From Our Instructors
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-warning fill-warning" />
                  ))}
                </div>
                <p className="text-white/70 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                       style={{ background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)' }}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="text-white font-medium">{testimonial.author}</div>
                    <div className="text-white/40 text-sm">{testimonial.role}</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <span className="text-success text-sm font-semibold">{testimonial.earnings}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh-strong" />
        
        <div className="container-luxury relative">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <div className="accent-line mx-auto mb-5" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Apply to Become an Instructor
              </h2>
              <p className="mt-3 text-white/40">
                Tell us about yourself and your expertise
              </p>
            </motion.div>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card text-center py-12"
              >
                <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-success" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Application Submitted!</h3>
                <p className="text-white/50 max-w-md mx-auto">
                  Thank you for your interest in becoming an instructor. Our team will review 
                  your application and get back to you within 48 hours.
                </p>
                <Link to="/" className="btn-secondary mt-8 inline-flex">
                  Return Home
                </Link>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="card space-y-6"
              >
                {isAuthenticated && user && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-slate/10 border border-slate/20">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                         style={{ background: 'linear-gradient(135deg, #2a4a5b 0%, #6c7e8d 100%)' }}>
                      {user.firstName.charAt(0)}
                    </div>
                    <div>
                      <div className="text-white font-medium">{user.firstName} {user.lastName}</div>
                      <div className="text-white/40 text-sm">{user.email}</div>
                    </div>
                    <Sparkles className="w-4 h-4 text-slate-light ml-auto" />
                  </div>
                )}

                <div>
                  <label className="label">Area of Expertise *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Web Development, Data Science, UI/UX Design"
                    value={formData.expertise}
                    onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="label">Years of Experience *</label>
                  <select
                    className="input"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    required
                  >
                    <option value="">Select experience level</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>

                <div>
                  <label className="label">Course Idea *</label>
                  <textarea
                    className="input min-h-[120px]"
                    placeholder="Describe your course idea and target audience..."
                    value={formData.courseIdea}
                    onChange={(e) => setFormData({ ...formData, courseIdea: e.target.value })}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">LinkedIn Profile</label>
                    <input
                      type="url"
                      className="input"
                      placeholder="https://linkedin.com/in/..."
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Personal Website</label>
                    <input
                      type="url"
                      className="input"
                      placeholder="https://..."
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Submit Application
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </button>
                </div>

                <p className="text-white/30 text-xs text-center">
                  By submitting, you agree to our{' '}
                  <Link to="/terms" className="text-coral hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-coral hover:underline">Privacy Policy</Link>
                </p>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

